import React, { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Mail, X } from 'lucide-react'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { authAPI } from '../../api/axios'
import toast from 'react-hot-toast'

function VerificationBanner() {
  const { user } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const [sending, setSending] = useState(false)

  if (!user || user.email_verified || dismissed) return null

  const handleResend = async () => {
    setSending(true)
    try {
      await authAPI.resendVerification()
      toast.success('Verification email sent! Check your inbox.')
    } catch {
      toast.error('Failed to send verification email')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="bg-gold-50 border-b border-gold-200 px-4 md:px-6 lg:px-8 py-2.5 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-gold-800">
        <Mail size={14} />
        <span>Please verify your email address.</span>
        <button onClick={handleResend} disabled={sending} className="font-semibold underline hover:no-underline">
          {sending ? 'Sending...' : 'Resend email'}
        </button>
      </div>
      <button onClick={() => setDismissed(true)} className="text-gold-600 hover:text-gold-800">
        <X size={14} />
      </button>
    </div>
  )
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isDark } = useTheme()

  // Dark mode is scoped to the authenticated app shell. Apply it here (and
  // strip it on unmount) so logged-out pages always render in the light theme.
  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', isDark)
    return () => root.classList.remove('dark')
  }, [isDark])

  return (
    <div className="app-layout grain">
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-forest-900/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="app-main">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />
        <VerificationBanner />
        <div className="flex-1 p-4 md:p-6 lg:p-8 page-enter">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
